import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatCard, Icon } from '../components/Icon';
import { StoredAttachments } from '../components/StoredAttachments';
import { useModalEscape } from '../lib/useModalEscape';
import {
  AuditTrail, DeleteTicketDialog, EditTicketModal, IconAction, ReplyItem, RestoreControl,
} from '../components/SupportManage';
import {
  SUPPORT_MANAGE, NEEDS_WRITE_REASON, parseCapabilities, ticketRef, fmtDateTime, isMissingEndpoint,
  type Capabilities,
} from '../lib/supportManage';
import { parseTicketAttachments, parseStoredAttachments, attachmentCount, formatBytes } from '../lib/ticketAttachments';

/** "5 Sept 2026" — date only, en-GB (gives "Sept"). */
function fmtDateOnly(s: string): string {
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface Ticket {
  id: string; title: string; type: string; severity: string; status: string;
  priority: string | null; account_id: string | null; account_email?: string | null;
  description?: string | null; response_count?: number; created_at: string;
  /** Files attached to the ticket; absent on an older backend. */
  attachment_count?: number | null;
  /** Support-manage fields; absent on an older backend. */
  assigned_to?: string | null;
  deleted_at?: string | null;
  restorable_until?: string | null;
}
interface Reply {
  id: string; author_type: string; author_name?: string; created_at: string;
  /** Null for a deleted message unless the caller may see it (audit view). */
  content: string | null;
  author_id?: string | null; edited_at?: string | null; deleted_at?: string | null; deleted_by_name?: string | null;
}

const STATUSES = ['open', 'in_progress', 'resolved', 'closed'];
const PRIORITIES = ['urgent', 'high', 'medium', 'low'];
const TYPES = ['bug', 'feature', 'question'];

// Ticket type shows as text, not an emoji/icon — per the portal's no-emoji,
// text-for-meaning convention (legacy used emoji; the new portal doesn't).
const PRIORITY_COLORS: Record<string, string> = { urgent: '#EF4444', high: '#F0C666', medium: '#3B82F6', low: '#AFAFAF' };
const RECORDING_BASE = 'https://api.wpcursor.com/api/upload/serve';

/** A paperclip and the file count, for a ticket row. Nothing when there are none. */
function AttachmentBadge({ count }: { count: unknown }) {
  const n = attachmentCount(count);
  if (n === 0) return null;
  const label = `${n} attachment${n === 1 ? '' : 's'}`;
  return (
    <span className="attach-badge muted small" role="img" aria-label={label} title={label} data-testid="attachment-badge">
      <Icon name="paperclip" size={14} /><span aria-hidden="true">{n}</span>
    </span>
  );
}

export default function Support() {
  const { can } = useAuth();
  const canWrite = can('support', 'write');
  /* support_manage: read = see deleted tickets + the audit trail; write = edit,
     delete and restore tickets and edit/delete messages. */
  const canManageRead = can(SUPPORT_MANAGE, 'read');
  const canManageWrite = can(SUPPORT_MANAGE, 'write');
  const [showDeleted, setShowDeleted] = useState(false);
  /* Set once the backend shows it does not have the new routes: every new
     control then disappears and the page behaves as it did before. */
  const [manageOff, setManageOff] = useState(false);
  const [editing, setEditing] = useState<Ticket | null>(null);
  const [deleting, setDeleting] = useState<Ticket | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState(''); const [typeF, setTypeF] = useState('');
  const [q, setQ] = useState('');
  /* `?ticket=<id>` opens that ticket — it is what the Slack support channel's
     "Open ticket" button links to. */
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState<string | null>(() => searchParams.get('ticket'));
  /* A linked ticket that is not in the loaded list. The list is the latest 200,
     so a customer replying on an older ticket links to one that isn't in it —
     without this the modal would silently fail to open. */
  const [linked, setLinked] = useState<Ticket | null>(null);
  const [linkError, setLinkError] = useState('');
  const [view, setView] = useState<'latest' | 'grouped'>('latest');
  const [groupQ, setGroupQ] = useState('');
  const [openAcct, setOpenAcct] = useState<string | null>(null);

  async function load(withDeleted: boolean = showDeleted) {
    setLoading(true);
    try {
      const d = await backendApi.get<{ tickets: Ticket[] }>(
        '/tickets/admin/list?take=200' + (withDeleted ? '&includeDeleted=1' : ''));
      setTickets(d.tickets || []); setError('');
    } catch (err: any) {
      if (withDeleted) {
        // Could not list deleted tickets: fall back to the normal list.
        setShowDeleted(false);
        if (isMissingEndpoint(err) || (err as ApiError).status === 400) setManageOff(true);
        setError(`Could not show deleted tickets: ${err.message}`);
        setLoading(false);
        void load(false);
        return;
      }
      setError((err as ApiError).isForbidden ? 'You do not have access to support.' : err.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  function toggleDeleted(on: boolean) {
    setShowDeleted(on);
    void load(on);
  }

  useEffect(() => {
    if (!open || loading || tickets.some((t) => t.id === open) || linked?.id === open) return;
    let cancelled = false;
    backendApi.get<{ ticket: Ticket }>(`/tickets/admin/${encodeURIComponent(open)}`)
      .then((d) => { if (!cancelled && d?.ticket) { setLinked(d.ticket); setLinkError(''); } })
      .catch((err: any) => {
        if (cancelled) return;
        setLinkError((err as ApiError).status === 404
          ? 'That ticket could not be found — it may have been deleted.'
          : `Could not open that ticket: ${err.message}`);
      });
    return () => { cancelled = true; };
  }, [open, loading, tickets, linked]);

  /* A ticket opened by link that is not in the list is held separately; after a
     change it has to be fetched again or the modal keeps showing the old copy. */
  function refreshLinked(id: string) {
    backendApi.get<{ ticket: Ticket }>(`/tickets/admin/${encodeURIComponent(id)}`)
      .then((d) => { if (d?.ticket) setLinked(d.ticket); })
      .catch(() => {});
  }
  function afterChange(id?: string) {
    void load();
    if (id && linked?.id === id) refreshLinked(id);
  }

  /* Closing clears `?ticket=` as well, so a refresh or Back doesn't reopen it. */
  function closeTicket() {
    setOpen(null);
    setLinkError('');
    if (searchParams.has('ticket')) {
      const next = new URLSearchParams(searchParams);
      next.delete('ticket');
      setSearchParams(next, { replace: true });
    }
  }

  /* Deleted tickets are never counted or grouped; they only appear in the
     "Show deleted" list. */
  const live = useMemo(() => tickets.filter((t) => !t.deleted_at), [tickets]);
  /* The new controls need a backend that sends `deleted_at` on list rows
     (an empty list cannot tell, and has nothing to act on anyway). */
  const manageAvailable = !manageOff && (tickets.length === 0 || tickets.some((t) => t.deleted_at !== undefined));

  const stats = useMemo(() => ({
    open: live.filter((t) => t.status === 'open').length,
    progress: live.filter((t) => t.status === 'in_progress').length,
    resolved: live.filter((t) => t.status === 'resolved' || t.status === 'closed').length,
  }), [live]);

  const needle = q.trim().toLowerCase();
  const filtered = useMemo(() => [...(showDeleted ? tickets : live)]
    .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at)) // latest on top
    .filter((t) =>
      (!statusF || t.status === statusF) && (!typeF || t.type === typeF) &&
      (!needle ||
        (t.title || '').toLowerCase().includes(needle) ||
        (t.account_email || '').toLowerCase().includes(needle) ||
        (t.description || '').toLowerCase().includes(needle))),
    [tickets, live, showDeleted, statusF, typeF, needle]);

  // Grouped view: one row per account/email with per-status counts.
  const groups = useMemo(() => {
    const m = new Map<string, Ticket[]>();
    live.forEach((t) => {
      const key = t.account_email || t.account_id || '—';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(t);
    });
    return [...m.entries()].map(([key, ts]) => ({
      key,
      total: ts.length,
      open: ts.filter((t) => t.status === 'open').length,
      progress: ts.filter((t) => t.status === 'in_progress').length,
      resolved: ts.filter((t) => t.status === 'resolved').length,
      closed: ts.filter((t) => t.status === 'closed').length,
      latest: ts.reduce((mx, t) => (+new Date(t.created_at) > +new Date(mx) ? t.created_at : mx), ts[0].created_at),
    })).sort((a, b) => b.total - a.total);
  }, [live]);
  const gNeedle = groupQ.trim().toLowerCase();
  const groupsFiltered = groups.filter((g) => !gNeedle || g.key.toLowerCase().includes(gNeedle));
  const acctTickets = openAcct ? live.filter((t) => (t.account_email || t.account_id || '—') === openAcct) : [];

  return (
    <>
      <header className="page-head"><div><h1>Support Tickets</h1><p className="muted">Customer issues and questions.</p></div></header>

      <div className="stat-row">
        <StatCard icon="ticket" tint="amber" value={stats.open} label="Open Tickets" />
        <StatCard icon="clock" tint="purple" value={stats.progress} label="In Progress" />
        <StatCard icon="check" tint="green" value={stats.resolved} label="Resolved" />
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {linkError && <div className="alert alert-error">{linkError}</div>}

      <div className="seg" style={{ marginBottom: 12 }}>
        <button className={'seg-btn' + (view === 'latest' ? ' on' : '')} onClick={() => setView('latest')}>Latest</button>
        <button className={'seg-btn' + (view === 'grouped' ? ' on' : '')} onClick={() => setView('grouped')}>Group by Email</button>
      </div>

      {view === 'latest' && (
        <>
          <div className="card">
            <div className="filter-bar">
              <select value={statusF} onChange={(e) => setStatusF(e.target.value)}>
                <option value="">All Status</option>{STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
              <select value={typeF} onChange={(e) => setTypeF(e.target.value)}>
                <option value="">All Types</option>{TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <input id="tickets-search" type="search" placeholder="Search subject, email, description…"
                value={q} onChange={(e) => setQ(e.target.value)} />
              {canManageRead && manageAvailable && (
                <label className="small" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={showDeleted} onChange={(e) => toggleDeleted(e.target.checked)} />
                  Show deleted
                </label>
              )}
            </div>
          </div>
          <div className="card">
            <h2 className="card-title">All Tickets</h2>
            {loading ? <p className="muted">Loading…</p> : filtered.length === 0 ? <p className="muted">No tickets match.</p> : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Type</th><th>Title</th><th>Account</th><th>Status</th><th>Priority</th><th>Responses</th><th>Created</th>{manageAvailable && <th><span className="sr-only">Actions</span></th>}</tr></thead>
                  <tbody>
                    {filtered.map((t) => (
                      <tr key={t.id} className={'clickable-row' + (t.deleted_at ? ' row-deleted' : '')} onClick={() => setOpen(t.id)}
                        data-deleted={t.deleted_at ? 'true' : undefined} style={t.deleted_at ? { opacity: 0.65 } : undefined}>
                        <td><span className="badge badge-gray">{t.type}</span></td>
                        <td>
                          <div className="ticket-title-row">
                            <div className="ticket-title" title={t.title}>{t.title}</div>
                            {t.deleted_at && <span className="badge badge-gray" data-testid="deleted-badge">Deleted {fmtDateOnly(t.deleted_at)}</span>}
                            <AttachmentBadge count={t.attachment_count} />
                          </div>
                          <span className="muted small block">severity: {t.severity || '—'}</span>
                        </td>
                        <td className="muted">{t.account_email || t.account_id || '—'}</td>
                        <td><span className={'badge ' + (t.status === 'resolved' || t.status === 'closed' ? 'badge-green' : t.status === 'in_progress' ? 'badge-purple' : 'badge-amber')}>{t.status.replace('_', ' ')}</span></td>
                        <td>{t.priority ? <span style={{ color: PRIORITY_COLORS[t.priority] || '#AFAFAF', fontWeight: 500, fontSize: 13 }}>{t.priority}</span> : '—'}</td>
                        <td className="mono">{t.response_count || 0}</td>
                        <td className="muted">{fmtDateOnly(t.created_at)}</td>
                        {manageAvailable && (
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {t.deleted_at ? (
                              <RestoreControl ticket={t} canWrite={canManageWrite} compact
                                onRestored={() => afterChange(t.id)} onMissing={() => setManageOff(true)} />
                            ) : (
                              <>
                                <IconAction label={`Edit ticket ${ticketRef(t)}`} icon="pencil"
                                  disabledReason={canManageWrite ? undefined : NEEDS_WRITE_REASON}
                                  onClick={() => setEditing(t)} />
                                <IconAction label={`Delete ticket ${ticketRef(t)}`} icon="trash" danger
                                  disabledReason={canManageWrite ? undefined : NEEDS_WRITE_REASON}
                                  onClick={() => setDeleting(t)} />
                              </>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {view === 'grouped' && (
        <>
          <div className="card">
            <div className="filter-bar">
              <input type="search" placeholder="Search account / email…" value={groupQ} onChange={(e) => setGroupQ(e.target.value)} style={{ flex: '1 1 240px' }} />
            </div>
          </div>
          <div className="card">
            <h2 className="card-title">Grouped by Email</h2>
            {loading ? <p className="muted">Loading…</p> : groupsFiltered.length === 0 ? <p className="muted">No accounts match.</p> : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Account</th><th>Tickets</th><th>Open</th><th>In progress</th><th>Resolved</th><th>Closed</th><th>Latest</th></tr></thead>
                  <tbody>
                    {groupsFiltered.map((g) => (
                      <tr key={g.key} className="clickable-row" onClick={() => setOpenAcct(g.key)}>
                        <td>{g.key}</td>
                        <td className="mono">{g.total}</td>
                        <td className="mono">{g.open || '—'}</td>
                        <td className="mono">{g.progress || '—'}</td>
                        <td className="mono">{g.resolved || '—'}</td>
                        <td className="mono">{g.closed || '—'}</td>
                        <td className="muted">{fmtDateOnly(g.latest)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {openAcct && (
        <AccountTicketsModal email={openAcct} tickets={acctTickets}
          onOpenTicket={(id) => setOpen(id)} onClose={() => setOpenAcct(null)} />
      )}

      {open && (() => {
        const t = tickets.find((x) => x.id === open) || (linked?.id === open ? linked : null);
        return t ? (
          <TicketModal ticket={t} canWrite={canWrite} canManageRead={canManageRead} canManageWrite={canManageWrite}
            manageOff={manageOff} onMissing={() => setManageOff(true)}
            onClose={closeTicket} onChanged={() => afterChange(t.id)} onDeleted={() => { closeTicket(); afterChange(t.id); }} />
        ) : null;
      })()}

      {editing && (
        <EditTicketModal ticket={editing} onMissing={() => setManageOff(true)} onClose={() => setEditing(null)}
          onSaved={() => { const id = editing.id; setEditing(null); afterChange(id); }} />
      )}
      {deleting && (
        <DeleteTicketDialog ticket={deleting} onMissing={() => setManageOff(true)} onCancel={() => setDeleting(null)}
          onDeleted={() => { const id = deleting.id; setDeleting(null); if (open === id) closeTicket(); afterChange(id); }} />
      )}
    </>
  );
}

/** Grouped view: one account's tickets → click a row to open the ticket modal. */
function AccountTicketsModal({ email, tickets, onOpenTicket, onClose }:
  { email: string; tickets: Ticket[]; onOpenTicket: (id: string) => void; onClose: () => void }) {
  useModalEscape(onClose);
  const sorted = [...tickets].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
  return createPortal(
    <div className="modal-scrim" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="modal account-modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>{email} — {tickets.length} ticket{tickets.length === 1 ? '' : 's'}</h2>
          <button className="btn-icon conv-modal-close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="account-modal-body">
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Type</th><th>Title</th><th>Status</th><th>Priority</th><th>Created</th></tr></thead>
              <tbody>
                {sorted.map((t) => (
                  <tr key={t.id} className="clickable-row" onClick={() => onOpenTicket(t.id)}>
                    <td><span className="badge badge-gray">{t.type}</span></td>
                    <td>
                      <div className="ticket-title-row">
                        <div className="ticket-title" title={t.title}>{t.title}</div>
                        <AttachmentBadge count={t.attachment_count} />
                      </div>
                    </td>
                    <td><span className={'badge ' + (t.status === 'resolved' || t.status === 'closed' ? 'badge-green' : t.status === 'in_progress' ? 'badge-purple' : 'badge-amber')}>{t.status.replace('_', ' ')}</span></td>
                    <td>{t.priority ? <span style={{ color: PRIORITY_COLORS[t.priority] || '#AFAFAF', fontWeight: 500, fontSize: 13 }}>{t.priority}</span> : '—'}</td>
                    <td className="muted">{fmtDateOnly(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>, document.body);
}

/** The diagnostics as pretty JSON. A string that is not valid JSON is shown as
 *  it is — a malformed value must not crash the modal. */
function prettyDiagnostics(d: unknown): string {
  if (typeof d === 'string') {
    try { return JSON.stringify(JSON.parse(d), null, 2); } catch { return d; }
  }
  return JSON.stringify(d, null, 2);
}

function TicketModal({ ticket, canWrite, canManageRead, canManageWrite, manageOff, onMissing, onClose, onChanged, onDeleted }:
  { ticket: Ticket; canWrite: boolean; canManageRead: boolean; canManageWrite: boolean; manageOff: boolean;
    onMissing: () => void; onClose: () => void; onChanged: () => void; onDeleted: () => void }) {
  const { user } = useAuth();
  /* What the backend says this caller may do here; null on a backend that does
     not send it, in which case none of the new controls are shown. */
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [editingTicket, setEditingTicket] = useState(false);
  const [deletingTicket, setDeletingTicket] = useState(false);
  const [auditKey, setAuditKey] = useState(0);
  const [replies, setReplies] = useState<Reply[] | null>(null);
  const [detail, setDetail] = useState<any>(null);
  /* The backend's own list of files; null when it did not send the field (an
     older backend), which is the only case the diagnostics fallback applies. */
  const [storedFiles, setStoredFiles] = useState<ReturnType<typeof parseStoredAttachments>>(null);
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');

  async function loadDetail() {
    try {
      const d = await backendApi.get<{ ticket: any; responses: Reply[]; attachments?: unknown; capabilities?: unknown }>(`/tickets/admin/${ticket.id}`);
      setDetail(d.ticket); setReplies(d.responses || []);
      setCaps(parseCapabilities(d.capabilities ?? d.ticket?.capabilities));
      setAuditKey((k) => k + 1);
      setStoredFiles(parseStoredAttachments(d.ticket?.attachments ?? d.attachments));
    } catch { setReplies([]); }
  }
  useEffect(() => { void loadDetail(); }, [ticket.id]);
  useModalEscape(onClose);

  /* Reloads this modal and the list after a manage action. */
  function changed() { onChanged(); void loadDetail(); }
  const deletedAt: string | null = detail?.deleted_at ?? ticket.deleted_at ?? null;
  const isDeleted = !!deletedAt;
  const manageShown = !manageOff && caps !== null;
  const shown: Ticket = { ...ticket, ...(detail || {}), id: ticket.id, title: detail?.title ?? ticket.title };

  async function patch(path: string, body: any) {
    setError('');
    try { await backendApi.patch(`/tickets/admin/${ticket.id}/${path}`, body); onChanged(); await loadDetail(); }
    catch (err: any) { setError(err.message); }
  }
  async function send() {
    if (!reply.trim()) return;
    setError('');
    try { await backendApi.post(`/tickets/admin/${ticket.id}/responses`, { content: reply }); setReply(''); await loadDetail(); onChanged(); }
    catch (err: any) { setError(err.message); }
  }

  return createPortal(
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal account-modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>{ticketRef(ticket)}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {manageShown && !isDeleted && (
              <>
                {(!canManageWrite || caps!.canEdit) && (
                  <IconAction label="Edit ticket" icon="pencil" testId="modal-edit"
                    disabledReason={canManageWrite ? undefined : NEEDS_WRITE_REASON}
                    onClick={() => setEditingTicket(true)} />
                )}
                {(!canManageWrite || caps!.canDelete) && (
                  <IconAction label="Delete ticket" icon="trash" danger testId="modal-delete"
                    disabledReason={canManageWrite ? undefined : NEEDS_WRITE_REASON}
                    onClick={() => setDeletingTicket(true)} />
                )}
              </>
            )}
            <button className="btn-icon conv-modal-close" aria-label="Close" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="account-modal-body">
        {error && <div className="alert alert-error">{error}</div>}
        {isDeleted && (
          <div className="alert alert-error" role="status" data-testid="deleted-banner"
            style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>This ticket was deleted on {fmtDateTime(deletedAt)}{detail?.deleted_by ? ` by ${detail.deleted_by}` : ''}. The customer cannot see it.</span>
            {manageShown && (
              <RestoreControl ticket={shown} canWrite={canManageWrite} onMissing={onMissing}
                onRestored={changed} />
            )}
          </div>
        )}
        {detail && (
          <div className="meta-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px', marginBottom: 14 }}>
            <div><strong>Type:</strong> {detail.type || '—'}</div>
            <div><strong>Severity:</strong> {detail.severity || '—'}</div>
            <div><strong>Site:</strong> <span className="muted">{detail.site_url || '—'}</span></div>
            <div><strong>Plugin:</strong> <span className="mono">{detail.plugin_version || '—'}</span></div>
            <div><strong>Plan:</strong> {detail.plan_id || '—'}</div>
            <div><strong>Priority:</strong>{' '}
              {detail.priority
                ? <span style={{ color: PRIORITY_COLORS[detail.priority] || '#AFAFAF', fontWeight: 500 }}>{detail.priority}</span>
                : '—'}
            </div>
          </div>
        )}
        {detail?.description && <p className="muted" style={{ marginBottom: 14, whiteSpace: 'pre-wrap' }}>{detail.description}</p>}
        {detail?.diagnostics != null && (
          <details style={{ marginBottom: 14 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>Diagnostics</summary>
            <pre className="mono" style={{ marginTop: 8, padding: 12, borderRadius: 6, overflowX: 'auto', fontSize: 12, background: 'var(--color-surface-overlay)' }}>
              {prettyDiagnostics(detail.diagnostics)}
            </pre>
          </details>
        )}
        {(() => {
          /* The backend lists the files itself: show those, fetched with this admin's
             session, and never read diagnostics (client-supplied JSON that may
             hold forged links). */
          if (storedFiles) return <StoredAttachments ticketId={ticket.id} files={storedFiles} />;
          /* Older backend, no `attachments` field: the user-app wrote signed links
             into diagnostics.attachments. Replies carry none. Nothing renders when
             there are none. */
          const files = parseTicketAttachments(detail?.diagnostics);
          if (files.length === 0) return null;
          return (
            <div style={{ marginBottom: 14 }} data-testid="ticket-attachments">
              <strong style={{ fontSize: 13 }}>Attachments ({files.length})</strong>
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                {files.map((f, i) => (
                  <div key={i} style={{ maxWidth: '100%' }}>
                    {f.kind === 'image' ? (
                      <a href={f.url} target="_blank" rel="noopener noreferrer" title={`Open ${f.name} full size`}>
                        <img src={f.url} alt={f.name} loading="lazy" referrerPolicy="no-referrer"
                          style={{ display: 'block', maxWidth: 320, maxHeight: 240, borderRadius: 8, background: 'var(--color-surface-overlay)' }} />
                      </a>
                    ) : f.kind === 'video' ? (
                      <video controls preload="none" src={f.url}
                        style={{ display: 'block', maxWidth: '100%', maxHeight: 300, borderRadius: 8, background: '#000' }} />
                    ) : null}
                    <div className="small" style={{ marginTop: 4 }}>
                      <a href={f.url} target="_blank" rel="noopener noreferrer">{f.name}</a>
                      {formatBytes(f.size) && <span className="muted"> · {formatBytes(f.size)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        {detail?.recording_path && (
          <div style={{ marginBottom: 14 }}>
            <strong style={{ fontSize: 13 }}>Screen Recording</strong>
            <div style={{ marginTop: 8 }}>
              <video controls style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8, background: '#000' }}
                src={`${RECORDING_BASE}/${detail.site_id || ''}/${detail.recording_path}`} />
            </div>
          </div>
        )}
        {canWrite && !isDeleted && (
          <div className="filter-bar" style={{ marginBottom: 16 }}>
            <label className="field" style={{ marginBottom: 0 }}><span>Status</span>
              <select value={ticket.status} onChange={(e) => patch('status', { status: e.target.value })}>
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}</select></label>
            <label className="field" style={{ marginBottom: 0 }}><span>Priority</span>
              <select value={ticket.priority || 'medium'} onChange={(e) => patch('priority', { priority: e.target.value })}>
                {PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></label>
          </div>
        )}
        <h4 className="detail-h">Conversation ({replies?.length ?? 0})</h4>
        {!replies ? <p className="muted small">Loading…</p> : replies.length === 0 ? <p className="muted small">No replies yet.</p> : (
          <ul className="plain-list">{replies.map((r) => (
            <ReplyItem key={r.id} ticketId={ticket.id} reply={r} onMissing={onMissing} onChanged={changed}
              /* Only staff messages, only when the backend lists this caller as
                 allowed (all of them, or their own), and never on a deleted ticket. */
              canManage={manageShown && !isDeleted && r.author_type === 'admin' && !r.deleted_at &&
                (caps!.canManageMessages || caps!.ownMessageIds.includes(r.id) || (!!r.author_id && r.author_id === user?.id))} />
          ))}</ul>
        )}
        {canWrite && !isDeleted && (
          <div style={{ marginTop: 12 }}>
            <textarea className="textarea" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" />
            <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={send}>Send reply</button>
          </div>
        )}
        {manageShown && canManageRead && <AuditTrail ticketId={ticket.id} refreshKey={auditKey} />}
        </div>
      </div>
      {editingTicket && (
        <EditTicketModal ticket={shown} onMissing={onMissing} onClose={() => setEditingTicket(false)}
          onSaved={() => { setEditingTicket(false); changed(); }} />
      )}
      {deletingTicket && (
        <DeleteTicketDialog ticket={shown} onMissing={onMissing} onCancel={() => setDeletingTicket(false)}
          onDeleted={() => { setDeletingTicket(false); onDeleted(); }} />
      )}
    </div>, document.body);
}
